import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import '../DesktopPostReviewPage/DesktopPostReviewPage.css';
import '../DesktopCompanyDNA/DesktopCompanyDNAPage.css';
import './DesktopAIStoragePage.css';
import useLanguage from '../../../hooks/useLanguage';
import { buildMediaCounters } from '../../Mobile/MobileAIStorage/utils';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { Skeleton } from '../../effects';
import aiStorageService from '../../../services/AIStorageService';

import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconAIFolder from '../../../assets/images/icons/icon-ai-folder.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import iconArrowLeft from '../../../assets/images/icons/icon-arrow-left.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconEdit from '../../../assets/images/icons/icon-edit.svg';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';

const formatBytes = (bytes, t) => {
  if (!bytes || Number(bytes) <= 0) {
    return `0 ${t('aiStorage.details.files.sizeUnits.1', { defaultValue: 'KB' })}`;
  }

  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const size = Number(bytes);
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[index]}`;
};

export const AlbumModal = ({
  isOpen,
  onClose,
  onCompleted,
  editMode = false,
  existingProject = null,
}) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && existingProject) {
      setTitle(existingProject.title || existingProject.name || '');
      setDescription(existingProject.description || '');
      setError(null);
    }
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setIsSaving(false);
      setError(null);
    }
  }, [existingProject, isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError(t('aiStorage.modals.create.errors.title', { defaultValue: 'Inserisci un titolo' }));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        title: title.trim(),
        description: description.trim(),
      };

      let normalized = null;
      if (editMode && existingProject?.id) {
        normalized = await aiStorageService.updateProject(existingProject.id, payload);
      } else {
        normalized = await aiStorageService.createProject(payload);
      }

      if (normalized && onCompleted) {
        onCompleted(normalized);
      }
      onClose();
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to save album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.modals.create.errors.submit', { error: message }));
    } finally {
      setIsSaving(false);
    }
  }, [description, editMode, existingProject?.id, onClose, onCompleted, t, title]);

  const handleBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget && !isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="loopino-ai-modal" onClick={handleBackdrop} role="dialog" aria-modal="true">
      <div className="loopino-ai-modal-card">
        <div className="loopino-ai-modal-header">
          <div className="loopino-ai-modal-title">
            <div className="loopino-ai-modal-icon">
              <img src={iconAi} alt={t('aiStorage.modals.create.alt.icon', { defaultValue: 'AI' })} />
            </div>
            <div>
              <p className="loopino-ai-modal-eyebrow">{t('aiStorage.modals.create.eyebrow', { defaultValue: 'Nuovo album' })}</p>
              <h3>{editMode ? t('aiStorage.modals.create.title.edit') : t('aiStorage.modals.create.title.create')}</h3>
            </div>
          </div>
          <button
            type="button"
            className="loopino-button loopino-ai-modal-close"
            data-variant="ghost"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t('aiStorage.actions.cancel', { defaultValue: 'Annulla' })}
          >
            <span aria-hidden>×</span>
          </button>
        </div>

        {error && (
          <div className="loopino-ai-modal-error">
            {error}
          </div>
        )}

        <div className="loopino-ai-modal-body">
          <label className="loopino-ai-modal-label" htmlFor="loopino-ai-album-title">
            {t('aiStorage.modals.create.labels.title')}
          </label>
          <input
            id="loopino-ai-album-title"
            className="loopino-ai-modal-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t('aiStorage.modals.create.placeholders.title')}
            disabled={isSaving}
          />

          <label className="loopino-ai-modal-label" htmlFor="loopino-ai-album-description">
            {t('aiStorage.modals.create.labels.description')}
          </label>
          <textarea
            id="loopino-ai-album-description"
            className="loopino-ai-modal-textarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('aiStorage.modals.create.placeholders.description')}
            rows={4}
            disabled={isSaving}
          />
        </div>

        <div className="loopino-ai-modal-actions">
          <button
            type="button"
            className="loopino-button"
            data-variant="ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('aiStorage.actions.cancel')}
          </button>
          <button
            type="button"
            className="loopino-button"
            data-variant="primary"
            onClick={handleSubmit}
            disabled={isSaving || !title.trim()}
          >
            {isSaving
              ? t('aiStorage.status.saving', { defaultValue: 'Salvataggio...' })
              : editMode
                ? t('aiStorage.actions.update')
                : t('aiStorage.actions.create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const FileDescriptionModal = ({ isOpen, onClose, onFileUpdated, file = null }) => {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && file) {
      setDescription(file.description || '');
      setError(null);
    }
    if (!isOpen) {
      setDescription('');
      setIsSaving(false);
      setError(null);
    }
  }, [file, isOpen]);

  const handleSave = useCallback(async () => {
    if (!file?.id) {
      setError(t('aiStorage.modals.fileDescription.errors.invalidFile', { defaultValue: 'File non valido' }));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const updated = await aiStorageService.updateAsset(file.id, {
        description: description.trim() || null,
      });

      if (onFileUpdated) {
        onFileUpdated(updated);
      }

      onClose();
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to update file description', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.modals.fileDescription.errors.update', { error: message }));
    } finally {
      setIsSaving(false);
    }
  }, [description, file, onClose, onFileUpdated, t]);

  const handleBackdrop = useCallback((event) => {
    if (event.target === event.currentTarget && !isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="loopino-ai-modal" onClick={handleBackdrop} role="dialog" aria-modal="true">
      <div className="loopino-ai-modal-card">
        <div className="loopino-ai-modal-header">
          <div className="loopino-ai-modal-title">
            <div className="loopino-ai-modal-icon">
              <img src={iconAi} alt={t('aiStorage.modals.fileDescription.alt.icon', { defaultValue: 'AI' })} />
            </div>
            <div>
              <p className="loopino-ai-modal-eyebrow">{t('aiStorage.modals.fileDescription.eyebrow', { defaultValue: 'Dettagli file' })}</p>
              <h3>{t('aiStorage.modals.fileDescription.title')}</h3>
            </div>
          </div>
          <button
            type="button"
            className="loopino-button"
            data-variant="ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('aiStorage.actions.cancel')}
          </button>
        </div>

        {error && (
          <div className="loopino-ai-modal-error">
            {error}
          </div>
        )}

        <div className="loopino-ai-modal-body">
          <label className="loopino-ai-modal-label" htmlFor="loopino-ai-file-description">
            {t('aiStorage.modals.fileDescription.label')}
          </label>
          <textarea
            id="loopino-ai-file-description"
            className="loopino-ai-modal-textarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('aiStorage.modals.fileDescription.placeholder')}
            rows={4}
            disabled={isSaving}
          />
        </div>

        <div className="loopino-ai-modal-actions">
          <button
            type="button"
            className="loopino-button"
            data-variant="ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('aiStorage.actions.cancel')}
          </button>
          <button
            type="button"
            className="loopino-button"
            data-variant="primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? t('aiStorage.status.saving') : t('aiStorage.actions.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const DesktopAIStorageDetailPage = ({
  projectId,
  onBack,
  onProjectUpdated,
  onProjectDeleted,
}) => {
  const { t, language } = useLanguage();
  const { confirm, notify } = useDesktopAlert();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageStates, setImageStates] = useState(() => new Map());
  const fileInputRef = useRef(null);

  const dateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (err) {
      console.warn('[DesktopAIStorage] Falling back to default date formatter', err);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }, [language]);

  const formatDate = useCallback((date) => {
    if (!date) return '--';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return '--';
    }
    return dateFormatter.format(parsed);
  }, [dateFormatter]);

  const loadProject = useCallback(async ({ silent = false } = {}) => {
    if (!projectId) return;

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      const nextProject = await aiStorageService.getProject(projectId);
      if (!nextProject) {
        throw new Error(t('aiStorage.details.errors.notFound'));
      }

      setProject(nextProject);
      if (onProjectUpdated) {
        onProjectUpdated(nextProject);
      }
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to load project', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.details.errors.load', { error: message }));
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [onProjectUpdated, projectId, t]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const uploadFilesToAlbum = useCallback(async (files) => {
    if (!projectId || !files || files.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);

    const toArray = Array.from(files).filter(Boolean);
    if (!toArray.length) {
      setIsUploading(false);
      return;
    }

    try {
      const updatedProject = await aiStorageService.uploadFiles(projectId, toArray);
      setProject(updatedProject);
      if (onProjectUpdated) {
        onProjectUpdated(updatedProject);
      }
    } catch (uploadErr) {
      console.error('[DesktopAIStorage] Failed to upload media', uploadErr);
      const message = uploadErr?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.details.errors.upload', { error: message }));
      await loadProject({ silent: true });
    } finally {
      setIsUploading(false);
    }
  }, [loadProject, onProjectUpdated, projectId, t]);

  const handleFileInputChange = useCallback((event) => {
    const { files } = event.target;
    if (!files || files.length === 0) {
      return;
    }

    uploadFilesToAlbum(files);
    event.target.value = '';
  }, [uploadFilesToAlbum]);

  const handleDeleteFile = useCallback(async (fileId) => {
    if (!projectId || !fileId) {
      return;
    }

    const confirmed = await confirm({
      title: t('aiStorage.details.messages.deleteFileConfirm', { defaultValue: 'Eliminare questo file?' }),
      confirmText: t('aiStorage.actions.delete'),
      cancelText: t('aiStorage.actions.cancel'),
      variant: 'warning',
    });

    if (!confirmed) return;

    setProject((prev) => {
      if (!prev) {
        return prev;
      }

      const filteredFiles = prev.files.filter((file) => file.id !== fileId);
      const counters = buildMediaCounters(filteredFiles);

      return {
        ...prev,
        files: filteredFiles,
        filesCount: filteredFiles.length,
        imagesCount: counters.imagesCount,
        videosCount: counters.videosCount,
      };
    });

    try {
      const updatedProject = await aiStorageService.removeAsset(projectId, fileId);
      setProject(updatedProject);
      if (onProjectUpdated) {
        onProjectUpdated(updatedProject);
      }
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to remove media from album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.details.errors.delete', { error: message }));
      await loadProject({ silent: true });
    }
  }, [confirm, loadProject, onProjectUpdated, projectId, t]);

  const handleEditProject = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleProjectUpdated = useCallback((updatedProject) => {
    if (!updatedProject) {
      return;
    }
    setProject((prev) => ({
      ...(prev || {}),
      ...updatedProject,
    }));
    if (onProjectUpdated) {
      onProjectUpdated(updatedProject);
    }
  }, [onProjectUpdated]);

  const handleDeleteProject = useCallback(async () => {
    const confirmed = await confirm({
      title: t('aiStorage.messages.deleteProjectConfirm'),
      confirmText: t('aiStorage.actions.delete', { defaultValue: 'Elimina' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' }),
      variant: 'warning',
    });

    if (!confirmed || !projectId) return;

    try {
      await aiStorageService.deleteProject(projectId);
      notify({
        title: t('aiStorage.details.messages.deleted', { defaultValue: 'Album eliminato' }),
        variant: 'info',
        duration: 2600,
      });
      if (onProjectDeleted) {
        onProjectDeleted(projectId);
      }
      if (onBack) {
        onBack();
      }
    } catch (err) {
      console.error('[DesktopAIStorage] Failed to delete album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.list.errors.delete', { error: message }));
    }
  }, [confirm, notify, onBack, onProjectDeleted, projectId, t]);

  const handleFileDescriptionClick = useCallback((file) => {
    setSelectedFile(file);
    setShowDescriptionModal(true);
  }, []);

  const handleFileUpdated = useCallback((updatedFile) => {
    if (!updatedFile) return;

    setProject((prev) => {
      if (!prev) return prev;
      const updatedFiles = (prev.files || []).map((file) => (
        file.id === updatedFile.id ? { ...file, ...updatedFile } : file
      ));
      return {
        ...prev,
        files: updatedFiles,
      };
    });
  }, []);

  const setImageStatus = useCallback((key, status) => {
    if (!key) return;
    setImageStates((prev) => {
      const current = prev.get(key);
      if (current === status) return prev;
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, []);

  const getImageLoadingProps = useCallback((source) => {
    const mapKey = source || 'unknown';
    const status = imageStates.get(mapKey) || 'loading';
    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageStatus(mapKey, 'loaded'),
      onError: () => setImageStatus(mapKey, 'error'),
    };
  }, [imageStates, setImageStatus]);

  const renderSkeleton = () => (
    <div className="loopino-desktop-shell loopino-ai-storage-shell">
      <header className="loopino-desktop-post-review-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <Skeleton width="50%" height={36} shimmer />
          <Skeleton width="70%" height={16} shimmer style={{ marginTop: 8 }} />
        </div>
        <div className="loopino-desktop-post-review-actions loopino-ai-storage-actions">
          <Skeleton width={120} height={44} shimmer />
          <Skeleton width={150} height={44} shimmer />
          <Skeleton width={150} height={44} shimmer />
        </div>
      </header>
      <div className="loopino-ai-detail-card loopino-card">
        <Skeleton width="40%" height={22} shimmer />
        <div className="loopino-ai-files-grid">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`file-skeleton-${index}`} className="loopino-ai-file-card is-skeleton">
              <div className="loopino-ai-file-thumb-wrapper">
                <Skeleton variant="rounded" width="100%" height="100%" shimmer style={{ height: '100%' }} />
              </div>
              <div className="loopino-ai-file-details">
                <Skeleton width="50%" height={14} shimmer />
                <Skeleton width="30%" height={12} shimmer />
              </div>
              <div className="loopino-ai-file-actions">
                <Skeleton width={40} height={40} shimmer />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return renderSkeleton();
  }

  if (!project) {
    return null;
  }

  return (
    <div className="loopino-desktop-shell loopino-ai-storage-shell">
      <header className="loopino-desktop-post-review-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{project.title || t('aiStorage.list.defaults.untitledAlbum')}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('aiStorage.details.subtitle', {
              defaultValue: 'Gestisci immagini e video caricati nel progetto, modifica le descrizioni e mantieni ordinato l\'archivio AI.',
            })}
          </p>
        </div>
        <div className="loopino-desktop-post-review-actions loopino-ai-storage-actions">
          <button
            type="button"
            className="loopino-button"
            data-variant="surface"
            onClick={onBack}
          >
            <img
              src={iconArrowLeft}
              alt={t('aiStorage.details.alt.back', { defaultValue: 'Indietro' })}
              className="loopino-ai-action-icon"
            />
            {t('common.actions.back', { defaultValue: 'Indietro' })}
          </button>
          <button
            type="button"
            className="loopino-button"
            data-variant="surface"
            onClick={handleEditProject}
          >
            <img src={iconEdit} alt="" className="loopino-ai-action-icon" />
            {t('aiStorage.actions.edit', { defaultValue: 'Modifica album' })}
          </button>
          <button
            type="button"
            className="loopino-button"
            data-variant="primary"
            onClick={handleUploadClick}
          >
            <img src={iconUpload} alt="" className="loopino-ai-action-icon" />
            {t('aiStorage.actions.upload', { defaultValue: 'Carica media' })}
          </button>
          <button
            type="button"
            className="loopino-button"
            data-variant="surface"
            onClick={handleDeleteProject}
          >
            <img src={iconDelete} alt="" className="loopino-ai-action-icon" />
            {t('aiStorage.actions.delete', { defaultValue: 'Elimina' })}
          </button>
        </div>
      </header>

      <div className="loopino-ai-side-panel loopino-glass-panel">
        <div className="loopino-ai-side-row">
          <p className="loopino-ai-storage-eyebrow">{t('aiStorage.details.meta.updated', { defaultValue: 'Aggiornato' })}</p>
          <h4>{formatDate(project.updatedAt)}</h4>
        </div>
        <div className="loopino-ai-side-row">
          <p className="loopino-ai-storage-eyebrow">{t('aiStorage.details.meta.created', { defaultValue: 'Creato' })}</p>
          <h4>{formatDate(project.createdAt)}</h4>
        </div>
        <div className="loopino-ai-side-row">
          <p className="loopino-ai-storage-eyebrow">{t('aiStorage.details.meta.storage', { defaultValue: 'Utilizzo' })}</p>
          <div className="loopino-ai-progress">
            <div
              className="loopino-ai-progress-bar"
              style={{
                width: project.currentTotalSizeKb && project.maxTotalSizeKb
                  ? `${Math.min(100, (project.currentTotalSizeKb / project.maxTotalSizeKb) * 100)}%`
                  : '12%',
              }}
            />
          </div>
          <p className="loopino-ai-storage-summary">
            {project.currentTotalSizeKb && project.maxTotalSizeKb
              ? `${formatBytes(project.currentTotalSizeKb * 1024, t)} / ${formatBytes(project.maxTotalSizeKb * 1024, t)}`
              : t('aiStorage.details.meta.storageUnknown', { defaultValue: 'Nessun limite definito' })}
          </p>
        </div>
      </div>

      {error && (
        <div className="loopino-ai-banner error">
          {error}
        </div>
      )}

      <div className="loopino-ai-detail-layout">
        <div className="loopino-ai-detail-card loopino-card">
          <div className="loopino-ai-detail-card-header">
            <div>
              <p className="loopino-ai-storage-eyebrow">{t('aiStorage.details.eyebrow', { defaultValue: 'File del progetto' })}</p>
              <h3>{t('aiStorage.details.title', { defaultValue: 'Contenuto album' })}</h3>
              <p className="loopino-ai-storage-summary">
                {project.description || t('aiStorage.details.noDescription', { defaultValue: 'Nessuna descrizione disponibile' })}
              </p>
            </div>
            <div className="loopino-ai-detail-meta">
              <span className="loopino-chip ghost">
                <img src={iconImage} alt="" />
                {project.imagesCount || 0} {t('aiStorage.list.alt.images', { defaultValue: 'Immagini' })}
              </span>
              <span className="loopino-chip ghost">
                <img src={iconVideo} alt="" />
                {project.videosCount || 0} {t('aiStorage.list.alt.videos', { defaultValue: 'Video' })}
              </span>
              <span className="loopino-chip ghost">
                {formatBytes(project.currentTotalSizeKb ? project.currentTotalSizeKb * 1024 : 0, t)}
              </span>
            </div>
          </div>

          {project.files?.length ? (
            <div className="loopino-ai-files-grid">
              {project.files.map((file) => {
                const isImage = file.type === 'image';
                const isVideo = file.type === 'video';
                const preview = file.url || file.thumbnailUrl || placeholderImage;
                const loadingProps = getImageLoadingProps(file.id || preview);
                const showSkeleton = isImage && loadingProps && loadingProps.status !== 'loaded';

                return (
                  <div
                    key={file.id}
                    className={`loopino-ai-file-card ${loadingProps?.className || ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFileDescriptionClick(file)}
                    onKeyDown={(event) => event.key === 'Enter' && handleFileDescriptionClick(file)}
                  >
                    <div className="loopino-ai-file-thumb-wrapper">
                      {isImage ? (
                        <img
                          src={preview}
                          alt={file.name}
                          className={['loopino-ai-file-thumb-media', loadingProps?.className].filter(Boolean).join(' ')}
                          loading="lazy"
                          decoding="async"
                          onLoad={loadingProps?.onLoad}
                          onError={(event) => {
                            loadingProps?.onError?.(event);
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = placeholderImage;
                          }}
                        />
                      ) : isVideo ? (
                        <div className="loopino-ai-file-thumb-media loopino-ai-file-thumb-video">
                          <video src={preview} muted preload="metadata" />
                        </div>
                      ) : (
                        <div className="loopino-ai-file-thumb-media loopino-ai-file-thumb-placeholder">
                          <img
                            src={iconImage}
                            alt={t('aiStorage.details.alt.file', { defaultValue: 'File' })}
                            className="loopino-ai-action-icon"
                          />
                        </div>
                      )}
                      {isVideo && (
                        <div className="loopino-ai-file-video-pill">
                          <img src={iconVideo} alt="" className="loopino-ai-action-icon" />
                        </div>
                      )}
                      {showSkeleton && (
                        <div className="loopino-ai-file-thumb-skeleton" aria-hidden>
                          <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                        </div>
                      )}
                    </div>
                    <div className="loopino-ai-file-details">
                      <div className="loopino-ai-file-title-row">
                        <p className="loopino-ai-file-name">
                          {file.name || t('aiStorage.details.files.untitled')}
                        </p>
                        {file.description && (
                          <span className="loopino-ai-file-pill">
                            {t('aiStorage.details.files.hasDescription', { defaultValue: 'Note' })}
                          </span>
                        )}
                      </div>
                      <p className="loopino-ai-file-meta">
                        {formatBytes(file.sizeBytes ?? file.size ?? (file.sizeKb ? file.sizeKb * 1024 : 0), t)}
                        {' · '}
                        {formatDate(file.createdAt || file.updatedAt)}
                      </p>
                    </div>
                    <div className="loopino-ai-file-actions">
                      <button
                        type="button"
                        className="loopino-ai-file-remove"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteFile(file.id);
                        }}
                        aria-label={t('aiStorage.details.accessibility.deleteFile')}
                      >
                        <img src={iconDelete} alt="" className="loopino-ai-action-icon" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="loopino-ai-empty loopino-glass-panel">
              <div className="loopino-ai-empty-icon">
                <img src={iconAIFolder} alt={t('aiStorage.list.alt.storage', { defaultValue: 'AI Storage' })} />
              </div>
              <h4>{t('aiStorage.details.empty.title', { defaultValue: 'Nessun file in questo album' })}</h4>
              <p>{t('aiStorage.details.empty.description', { defaultValue: 'Carica immagini o video per iniziare a popolare il progetto.' })}</p>
              <button type="button" className="loopino-button" data-variant="primary" onClick={handleUploadClick}>
                {t('aiStorage.actions.upload', { defaultValue: 'Carica media' })}
              </button>
            </div>
          )}
        </div>

      </div>

      {isUploading && (
        <div className="loopino-ai-upload-overlay">
          <div className="loopino-ai-upload-card">
            <div className="loopino-ai-upload-spinner" />
            <p>{t('aiStorage.status.uploading', { defaultValue: 'Caricamento in corso...' })}</p>
          </div>
        </div>
      )}

      <AlbumModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onCompleted={handleProjectUpdated}
        editMode
        existingProject={project}
      />

      <FileDescriptionModal
        isOpen={showDescriptionModal}
        onClose={() => setShowDescriptionModal(false)}
        onFileUpdated={handleFileUpdated}
        file={selectedFile}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: 'none' }}
        multiple
        onChange={handleFileInputChange}
      />
    </div>
  );
};

export default DesktopAIStorageDetailPage;
